import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { AutoGratuityLine, OrdersWithPayment } from 'src/app/models/order';
import { OrderService } from 'src/app/order.service';
import {MatDialog} from '@angular/material/dialog';
import {CustomTipDialogComponent} from './custom-tip-dialog/custom-tip-dialog.component';

@Component({
  selector: 'app-order-summary-tip',
  templateUrl: './tip.component.html',
  styleUrls: ['./tip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TipComponent implements OnInit {

  @Input() orderWithPayment:OrdersWithPayment;
  @Input() orderHash:string;

  tipPercent: number | 'custom' = 0;
  customTipAmount: number | null = null;
  @Output() refresh = new EventEmitter<any>();

  tips = [
    {"name": "18%", "value": 18},
    {"name": "20%", "value": 20},
    {"name": "25%", "value": 25},
    {"name": "Custom", "value": 'custom'}
  ]

  customTipHidden = true;

  /** ENG-5812: rule-based gratuity lines already on the check (inside the total). Shown as
   *  "Gratuity included", as the POS receipt does in place of its suggested percentages; the
   *  percent buttons here stay on the subtotal (updateTip / the backend both use sub_total). */
  get gratuityLines(): AutoGratuityLine[] {
    return (this.orderWithPayment?.payment?.auto_gratuities || []).filter(g => +g.amount > 0);
  }

  constructor(
    private orderService:OrderService,
    private dialog: MatDialog
  ) { }

  toggleCustomTip(){
    this.customTipHidden = !this.customTipHidden
  }

  calculateTip(tip: any) {
    // Toggle: clicking already-selected tip deselects it (sets tip to 0)
    if (tip.value === this.tipPercent) {
      this.tipPercent = null;
      this.customTipAmount = null;
      this.resetCustomLabel();
      this.orderWithPayment.updateTip(0);
      this.orderService.updateTip(this.orderHash, 0).subscribe(() => {
        this.refresh.emit(true);
      });
      return;
    }

    if (tip.value === 'custom') {
      this.openCustomTip();
      return;
    }

    this.tipPercent = tip.value;
    this.customTipAmount = null;
    this.resetCustomLabel();
    this.orderWithPayment.updateTip(tip.value);
    this.orderService.updateTip(this.orderHash, tip.value).subscribe(() => {
      this.refresh.emit(true);
    });
  }

  removeTip() {
    this.tipPercent = null;
    this.customTipAmount = null;
    this.resetCustomLabel();
    this.orderWithPayment.updateTip(0);
    this.orderService.updateTip(this.orderHash, 0).subscribe(() => {
      this.refresh.emit(true);
    });
  }

  /** Reset the Custom button label back to plain "Custom" */
  private resetCustomLabel() {
    const customTip = this.tips.find(t => t.value === 'custom');
    if (customTip) {
      customTip.name = 'Custom';
    }
  }
  

  calculateCustomTip(tip: number) {
    this.orderService.updateCustomTip(this.orderHash, tip).subscribe(() => {
      this.customTipAmount = tip;
      this.orderWithPayment.updateTipCustom(+tip);

      const storeTipOptions = this.orderWithPayment.getTipPercentOptions();
      if (storeTipOptions.length > 0) {
        this.tips = storeTipOptions.map(t => ({
          name: `${t.percent}%`,
          value: t.percent
        }));
      } else {
        this.tips = [
          {"name": "18%", "value": 18},
          {"name": "20%", "value": 20},
          {"name": "25%", "value": 25},
        ];
      }

      this.tips.push({ name: "Custom", value: "custom" });

      this.tipPercent = "custom";
      this.refresh.emit(true);
    });
  }

  ngOnInit() {
    const tipPercent = this.orderWithPayment.getTipPercent();
    const storeTipOptions = this.orderWithPayment.getTipPercentOptions();

    if (storeTipOptions.length > 0) {
      this.tips = storeTipOptions.map(t => ({
        name: `${t.percent}%`,
        value: t.percent
      }));

      const customTip = (this.orderWithPayment as any).custom_tip_amount;
      if (customTip && customTip > 0) {
        this.customTipAmount = customTip;
        this.tips.push({ name: "Custom", value: "custom" });

        this.tipPercent = "custom";
        return;
      } else {
        this.tips.push({ name: "Custom", value: "custom" });
      }
    }
  
    const isPreset = this.tips.some(t => t.value === tipPercent);
    this.tipPercent = isPreset ? tipPercent : (tipPercent > 0 ? 'custom' : null);
  
  }
  

  openCustomTip() {
    const dialogRef = this.dialog.open(CustomTipDialogComponent, {
      width: '320px',
      maxWidth: '85vw',
      panelClass: 'custom-tip-panel'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && result !== null && result !== '') {
        this.calculateCustomTip(result);
      }
    });
  }
  

}
